// Kirim dokumen ke pelanggan lewat WA (keputusan Apo 20 Jul: "tautan halaman").
//
// WhatsApp TIDAK bisa melampirkan berkas dari aplikasi web — yang bisa dikirim hanya teks.
// Jadi dokumen disimpan sebagai halaman di Storage, lalu TAUTAN bertanda-tangannya yang
// dikirim. Pelanggan membukanya di HP dan bisa menyimpan/mencetaknya sendiri jadi PDF
// lewat tombol bagikan bawaan HP.
//
// Tanda Terima Servis SENGAJA tidak ikut — per Apo itu dokumen cetak (ditandatangani di toko).
import { uploadFile, signedUrl } from '@core/storage';
import { openWA } from '@core/wa';
import { currentUser } from '@core/session';
import { addLog } from '@modules/intake/intake.repo';
import { waTpl, kebijakan } from '@core/settings';
import { fillTpl, esc, wa62, rp } from '@domain/format';
import { masaDetik } from '@domain/doclink';

const BUCKET = 'dokumen';

/**
 * Bungkus HTML dokumen (yang dirancang untuk kertas) agar nyaman dibaca di layar HP:
 * viewport, latar netral, dan lebar dibatasi. Isi dokumennya sendiri tidak diubah.
 */
function halamanHTML(judul: string, isi: string): string {
  return `<!doctype html><html lang="id"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(judul)}</title>
<style>
  body{margin:0;background:#eef2f6;font-family:Arial,Helvetica,sans-serif}
  .kertas{max-width:780px;margin:0 auto;background:#fff;padding:14px 16px;
    box-shadow:0 1px 6px rgba(0,0,0,.12)}
  @media print{body{background:#fff}.kertas{box-shadow:none;max-width:none;padding:0}}
</style></head><body><div class="kertas">${isi}</div></body></html>`;
}

export interface KirimDokOpts {
  no: string;                 // nomor servis
  outlet: string | null;      // folder storage → RLS cabang
  jenis: string;              // "nota servis" / "surat kuasa" — ikut ke nama berkas & isi pesan
  judul: string;              // judul halaman
  html: string;               // HTML dokumen (sudah ter-escape di documents.doc)
  waPelanggan: string;        // nomor tujuan
  nama: string; merk: string; tipe: string; total: number;
}

/**
 * Simpan dokumen → buat tautan → buka WA berisi tautan itu.
 * Tiap pengiriman membuat berkas BARU (nama acak) — dokumen yang sudah diterima pelanggan
 * tak pernah berubah isi di belakang layar.
 */
export async function kirimDokumenWA(o: KirimDokOpts): Promise<void> {
  if (!wa62(o.waPelanggan)) throw new Error('Nomor WA pelanggan belum terisi — lengkapi di data pelanggan dulu.');
  // Folder = cabang; tanpa cabang penyimpanan menolak dgn pesan teknis yang tak dipahami kasir.
  if (!o.outlet) throw new Error('Tiket ini belum punya cabang — lengkapi dulu sebelum mengirim dokumen.');
  const blob = new Blob([halamanHTML(o.judul, o.html)], { type: 'text/html' });
  const path = await uploadFile(BUCKET, o.outlet, blob, 'html', 'text/html');
  const asli = await signedUrl(BUCKET, path, masaDetik(kebijakan().dokLinkHari));
  // Supabase SELALU menyajikan berkas HTML sebagai `text/plain` (pengamanan mereka) — tautan
  // storage mentah akan memperlihatkan KODE, bukan nota. Karena itu yang dikirim adalah penampil
  // milik kita (`/d.html`) yang mengambil lalu menampilkan isinya.
  // Dikirim sebagai FRAGMENT (#u=), bukan query (?u=): `asli` adalah tautan bertanda-tangan yang
  // berlaku berbulan-bulan — siapa pun yang memegangnya bisa membuka nota pelanggan itu. Query
  // string tercatat di log akses hosting dan riwayat peramban; fragment tidak pernah dikirim ke
  // server sama sekali. Halaman lacak/garansi/survei/poin sudah memakai pola ini; d.html tertinggal.
  const url = `${location.origin}/d.html#u=${encodeURIComponent(asli)}`;
  // Jejak: kalau kasir salah pilih pelanggan, harus bisa ditelusuri dokumen mana yang terkirim,
  // kapan, oleh siapa, dan berkas mana yang perlu dihapus di Pengaturan → Penyimpanan.
  const hari = kebijakan().dokLinkHari;
  try {
    await addLog(o.no, 'Dokumen dikirim', `${o.jenis} dikirim ke WA pelanggan · tautan berlaku ${hari} hari · berkas ${path}`, currentUser()?.nama ?? '');
  } catch { /* jejak gagal jangan menggagalkan pengiriman */ }
  openWA(o.waPelanggan, fillTpl(waTpl().dokumenLink, {
    nama: o.nama || 'pelanggan', no: o.no, merk: o.merk, tipe: o.tipe,
    jenis: o.jenis, total: rp(o.total), link: url,
  }));
}
